// src/utils/firebase/releaseCards.js
// Releases a sale's cards from the user's collection once the sale completes.
// Named around releasing cards rather than any one marketplace so other
// modules (eBay order imports, Inventory) can reuse it.

import { collection, doc, getDoc, getDocs, updateDoc, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from './firebase.utils';
import { removeCardFromFirestore, addCardsToFirestore } from './collection';
import { logUserAction } from './activity';
import { normalizeForCompare } from '../textNormalization';
import { SALE_ACTIVITY_TYPES } from '../sales/sales.constants';

export const RELEASE_RESULTS = {
  REMOVED: 'removed',
  NOT_IN_COLLECTION: 'not_in_collection',
  NO_CARD_PATH: 'no_card_path'
};

const CARD_PATH_ORDER = ['sport', 'year', 'manufacturer', 'series', 'cardType', 'cardTypeCategory'];

export const hasCompleteCardPath = (cardPath) => (
  !!cardPath && CARD_PATH_ORDER.every((segment) => !!cardPath[segment])
);

/**
 * Resolves the collection location of a sale card.
 *
 * Cards promoted from the eBay queue carry a structured cardPath. Cards added
 * straight from the collection only carry the checklistPath string, so fall back
 * to parsing it — the two encode the same six segments.
 */
export const resolveCardPath = (card) => {
  if (hasCompleteCardPath(card?.cardPath)) return card.cardPath;
  if (!card?.checklistPath) return null;

  const segments = card.checklistPath.replace('/checklists/', '').split('/').filter(Boolean);
  if (segments.length < CARD_PATH_ORDER.length) return null;

  // Trailing segments (a cardNumber on some paths) are intentionally ignored.
  const cardPath = CARD_PATH_ORDER.reduce((acc, segment, index) => {
    acc[segment] = segments[index];
    return acc;
  }, {});

  return hasCompleteCardPath(cardPath) ? cardPath : null;
};

/**
 * Reads the sale's card snapshots.
 * @param {string} uid - User ID
 * @param {string} saleId - Sale document ID
 * @returns {Promise<Array<Object>>}
 */
export const getSaleCards = async (uid, saleId) => {
  const snap = await getDocs(collection(db, `users/${uid}/transactions/${saleId}/cards`));
  return snap.docs.map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }));
};

const getSaleLogContext = async (uid, saleId) => {
  const saleSnap = await getDoc(doc(db, `users/${uid}/transactions/${saleId}`));
  const saleData = saleSnap.exists() ? saleSnap.data() : {};
  return {
    saleId,
    title: (saleData.title && String(saleData.title).trim()) || 'Untitled listing',
    ebaySku: saleData.ebaySku ?? null,
    platform: saleData.platform ?? null
  };
};

const logSaleCollectionAction = (uid, actionType, { cards, prevCount, increment, summary, sale }) => {
  logUserAction(uid, actionType, {
    cards,
    prevCount,
    increment,
    summary,
    saleId: sale.saleId,
    title: sale.title,
    ebaySku: sale.ebaySku,
    platform: sale.platform
  });
};

const sameVariation = (a, b) => (
  normalizeForCompare(a.cardParallelName) === normalizeForCompare(b.cardParallelName)
  && (a.cardPrintNumber ?? null) === (b.cardPrintNumber ?? null)
  && (a.cardSupply ?? null) === (b.cardSupply ?? null)
);

const buildVariation = (variation) => ({
  cardParallelName: variation?.cardParallelName ?? null,
  cardColor: variation?.cardColor ?? null,
  cardQty: 1,
  cardPrintNumber: variation?.cardPrintNumber ?? null,
  cardSupply: variation?.cardSupply ?? null,
  cardOdds: variation?.cardOdds ?? null
});

/**
 * Accumulates one card into the nested structure addCardsToFirestore consumes,
 * which carries a running total at every level of the tree.
 */
export const addToQueueStructure = (queuedCards, cardPath, card) => {
  const { sport, year, manufacturer, series, cardType, cardTypeCategory } = cardPath;
  const cardNumber = card.cardNumber;

  const levels = [
    [sport],
    [sport, year],
    [sport, year, manufacturer],
    [sport, year, manufacturer, series],
    [sport, year, manufacturer, series, cardType],
    [sport, year, manufacturer, series, cardType, cardTypeCategory]
  ];

  queuedCards.totalCardCount = (queuedCards.totalCardCount || 0) + 1;

  levels.forEach((keys) => {
    const node = keys.reduce((acc, key) => {
      if (!acc[key]) acc[key] = { totalCardCount: 0 };
      return acc[key];
    }, queuedCards);
    node.totalCardCount += 1;
  });

  const category = levels[levels.length - 1].reduce((acc, key) => acc[key], queuedCards);
  const variation = buildVariation(card.variation);
  const existing = category[cardNumber];

  if (existing) {
    const match = existing.collectedCards.find((v) => sameVariation(v, variation));
    if (match) {
      match.cardQty += 1;
    } else {
      existing.collectedCards.push(variation);
    }
    existing.quantity += 1;
    return;
  }

  const entry = {
    cardNumber,
    name: card.name ?? [],
    team: card.team ?? [],
    cardAttributes: card.cardAttributes ?? [],
    quantity: 1,
    collectedCards: [variation]
  };

  // Custom categories intentionally carry no checklistPath, and the stored path
  // includes the card number, unlike the sale snapshot's category-level one.
  if (!String(cardTypeCategory).startsWith('custom:')) {
    entry.checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}/${cardNumber}`;
  }

  category[cardNumber] = entry;
};

/**
 * Removes each of a sale's cards from the user's collection.
 *
 * Runs sequentially on purpose: removeCardFromFirestore reads and then rewrites
 * four shared ancestor count documents, so cards from the same series would lose
 * count updates if released in parallel.
 *
 * Every processed card is flagged, making the operation idempotent and
 * resumable: a card is never decremented twice, and a run that fails partway
 * can be retried to finish the remainder.
 *
 * @param {string} uid - User ID
 * @param {string} saleId - Sale document ID
 * @param {Array<Object>} cards - Sale card snapshots (with id)
 * @returns {Promise<{removed: number, notInCollection: number, skipped: number, alreadyReleased: number}>}
 */
export async function releaseSaleCardsFromCollection(uid, saleId, cards) {
  const summary = { removed: 0, notInCollection: 0, skipped: 0, alreadyReleased: 0 };
  if (!uid || !saleId || !Array.isArray(cards) || cards.length === 0) return summary;

  // Track locally so a later sales log can record one before/after count
  // for the whole release, instead of one entry per card.
  const userSnap = await getDoc(doc(db, `users/${uid}`));
  let runningCount = (userSnap.exists() ? userSnap.data().collectionCardCount : 0) || 0;
  const prevCount = runningCount;
  const sale = await getSaleLogContext(uid, saleId);
  const removedCards = { totalCardCount: 0 };
  let processedThisRun = 0;

  for (const card of cards) {
    if (card.releasedFromCollection) {
      summary.alreadyReleased += 1;
      continue;
    }

    const cardRef = doc(db, `users/${uid}/transactions/${saleId}/cards/${card.id}`);
    const cardPath = resolveCardPath(card);

    processedThisRun += 1;

    if (!cardPath) {
      summary.skipped += 1;
      await updateDoc(cardRef, {
        releasedFromCollection: true,
        releaseResult: RELEASE_RESULTS.NO_CARD_PATH,
        releasedAt: Timestamp.now()
      });
      continue;
    }

    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = cardPath;
    let result = RELEASE_RESULTS.REMOVED;

    try {
      await removeCardFromFirestore(
        uid,
        card.variation ?? null,
        1,
        sport,
        year,
        manufacturer,
        series,
        cardType,
        cardTypeCategory,
        card.cardNumber,
        runningCount,
        card,
        true
      );
      addToQueueStructure(removedCards, cardPath, card);
      summary.removed += 1;
      runningCount = Math.max(0, runningCount - 1);
    } catch (error) {
      // Expected when the card was sold without ever being in the collection,
      // or when that variation has already been removed by hand.
      result = RELEASE_RESULTS.NOT_IN_COLLECTION;
      summary.notInCollection += 1;
    }

    await updateDoc(cardRef, {
      releasedFromCollection: true,
      releaseResult: result,
      releasedAt: Timestamp.now()
    });
  }

  await updateDoc(doc(db, `users/${uid}/transactions/${saleId}`), {
    collectionReleasedAt: Timestamp.now(),
    collectionReleaseSummary: summary,
    updatedAt: Timestamp.now()
  });

  if (processedThisRun > 0) {
    logSaleCollectionAction(uid, SALE_ACTIVITY_TYPES.SOLD, {
      cards: removedCards,
      prevCount,
      increment: summary.removed * -1,
      summary,
      sale
    });
  }

  return summary;
}

/**
 * Puts a sold sale's cards back into the collection, undoing a release.
 *
 * Only cards this sale actually removed are restored — anything that was
 * already missing from the collection is left alone. Every card's release flag
 * is cleared either way, so the sale returns to a clean pre-sold state and can
 * be marked sold again from scratch.
 *
 * @param {string} uid - User ID
 * @param {string} saleId - Sale document ID
 * @param {Array<Object>} cards - Sale card snapshots (with id)
 * @returns {Promise<{restored: number, notRestored: number}>}
 */
export async function restoreSaleCardsToCollection(uid, saleId, cards) {
  const summary = { restored: 0, notRestored: 0 };
  if (!uid || !saleId || !Array.isArray(cards) || cards.length === 0) return summary;

  const processed = cards.filter((card) => card.releasedFromCollection);
  if (processed.length === 0) return summary;

  const queuedCards = { totalCardCount: 0 };

  for (const card of processed) {
    const cardPath = card.releaseResult === RELEASE_RESULTS.REMOVED ? resolveCardPath(card) : null;
    if (!cardPath) {
      summary.notRestored += 1;
      continue;
    }
    addToQueueStructure(queuedCards, cardPath, card);
    summary.restored += 1;
  }

  const userSnap = await getDoc(doc(db, `users/${uid}`));
  const prevCount = (userSnap.exists() ? userSnap.data().collectionCardCount : 0) || 0;
  const sale = await getSaleLogContext(uid, saleId);

  if (summary.restored > 0) {
    await addCardsToFirestore(uid, queuedCards);
  }

  const batch = writeBatch(db);
  processed.forEach((card) => {
    batch.update(doc(db, `users/${uid}/transactions/${saleId}/cards/${card.id}`), {
      releasedFromCollection: false,
      releaseResult: null,
      releasedAt: null,
      restoredAt: Timestamp.now()
    });
  });
  batch.update(doc(db, `users/${uid}/transactions/${saleId}`), {
    collectionReleasedAt: null,
    collectionReleaseSummary: null,
    updatedAt: Timestamp.now()
  });
  await batch.commit();

  logSaleCollectionAction(uid, SALE_ACTIVITY_TYPES.RETURNED, {
    cards: queuedCards,
    prevCount,
    increment: summary.restored,
    summary,
    sale
  });

  return summary;
}
